import { useEffect, useState } from 'react';
import { subscribeSyncState } from '@/lib/firebaseSync';
import { UtensilsCrossed, Settings2, WifiOff } from 'lucide-react';

/**
 * SyncHealthBadge — Painel de Saúde do Sync.
 *
 * Exibe, de forma discreta no rodapé da sidebar, há quanto tempo cada
 * domínio crítico (Cardápio = `menuItems`, Configurações = `config`)
 * fez a última troca com o Firebase RTDB. Usa o valor mais recente entre
 * push (escrita local que subiu) e receive (snapshot remoto recebido).
 *
 * Estados:
 *   🟢 Sincronizado   — última troca há ≤ 60s
 *   🟡 Lento          — última troca entre 60s e 5min
 *   🔴 Offline / Stale — sem troca há > 5min OU sync não conectado
 *   ⚪ Aguardando     — sync ativo mas ainda sem nenhuma troca
 */
function formatRelative(ms) {
  if (!ms) return null;
  const diff = Date.now() - ms;
  if (diff < 0) return 'agora';
  if (diff < 5_000) return 'agora';
  if (diff < 60_000) return `há ${Math.floor(diff / 1000)}s`;
  if (diff < 3_600_000) return `há ${Math.floor(diff / 60_000)}min`;
  const h = Math.floor(diff / 3_600_000);
  return `há ${h}h`;
}

function severity(ms, online) {
  // v8.08 — DIAGNÓSTICO DO FALSO ALARME "parada aos 60s": este badge media
  // a IDADE DA ÚLTIMA MUDANÇA de cardápio/config — não a saúde da conexão.
  // Numa noite normal ninguém edita o cardápio, então aos 60s ficava "lento"
  // e aos 5min "SINCRONIZAÇÃO PARADA" com o socket PERFEITO. Agora:
  // conexão real (.info/connected + auth) manda; idade de dados é só
  // informativa e nunca pinta alarme com a conexão viva.
  if (!online) return 'offline';
  if (!ms) return 'waiting';
  return 'ok';
}

const DOMAINS = [
  { key: 'menuItems', label: 'Cardápio', Icon: UtensilsCrossed },
  { key: 'config', label: 'Configurações', Icon: Settings2 },
];

export default function SyncHealthBadge() {
  const [state, setState] = useState({ connected: false, initialized: false, authenticated: false, lastSync: {} });
  const [, setTick] = useState(0);

  useEffect(() => subscribeSyncState(setState), []);

  // Tick local de 1s para atualizar o "há Xs" sem precisar de re-emit do firebaseSync.
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  if (!state.initialized) return null; // sync desativado → nada a mostrar
  const online = state.connected && state.authenticated;

  // Status global pior caso entre os dois domínios.
  const items = DOMAINS.map((d) => {
    const entry = state.lastSync?.[d.key] || { push: 0, receive: 0 };
    const last = Math.max(entry.push || 0, entry.receive || 0);
    return { ...d, last, sev: severity(last, online) };
  });
  const order = { stale: 0, offline: 1, slow: 2, waiting: 3, ok: 4 };
  const worst = items.reduce((acc, it) => (order[it.sev] < order[acc] ? it.sev : acc), 'ok');

  let headerColor, headerLabel, dot;
  if (worst === 'offline' || worst === 'stale') {
    headerColor = 'bg-rose-50 border-rose-200 text-rose-700';
    headerLabel = !online ? 'Sincronização offline' : 'Sincronização parada';
    dot = 'bg-rose-500';
  } else if (worst === 'slow') {
    headerColor = 'bg-amber-50 border-amber-200 text-amber-800';
    headerLabel = 'Sincronização lenta';
    dot = 'bg-amber-500';
  } else if (worst === 'waiting') {
    headerColor = 'bg-slate-50 border-slate-200 text-slate-600';
    headerLabel = 'Aguardando sync';
    dot = 'bg-slate-400';
  } else {
    headerColor = 'bg-emerald-50 border-emerald-200 text-emerald-700';
    headerLabel = 'Sincronizado';
    dot = 'bg-emerald-500';
  }

  return (
    <div
      data-testid="sync-health-badge"
      className={`rounded-lg border ${headerColor} px-2.5 py-2 text-[10px] leading-tight`}
      title="Última troca com Firebase por domínio operacional"
    >
      <div className="flex items-center gap-1.5 font-bold uppercase tracking-wide mb-1.5">
        <span className={`inline-block w-1.5 h-1.5 rounded-full ${dot} ${worst === 'ok' ? 'animate-pulse' : ''}`} />
        {headerLabel}
      </div>
      <ul className="space-y-0.5">
        {items.map((it) => {
          const rel = it.last ? formatRelative(it.last) : '—';
          const itemColor =
            it.sev === 'ok' ? 'text-emerald-700'
              : it.sev === 'slow' ? 'text-amber-800'
                : it.sev === 'waiting' ? 'text-slate-500'
                  : 'text-rose-700';
          return (
            <li
              key={it.key}
              data-testid={`sync-health-${it.key}`}
              className={`flex items-center justify-between gap-2 ${itemColor}`}
            >
              <span className="flex items-center gap-1.5">
                {!online ? <WifiOff size={10} strokeWidth={2.5} /> : <it.Icon size={10} strokeWidth={2.5} />}
                {it.label}
              </span>
              <span className="font-mono tabular-nums">{!online ? 'offline' : rel}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
